import React, { useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LuMoreVertical, LuHeart, LuUser, LuFlag, LuBan, LuShieldOff, LuGift } from 'react-icons/lu';
import config from '../../Config';

/**
 * Menú "⋮" de opciones sobre otro usuario, como el ChatOptionsSheet de la app:
 * favoritos (solo cliente → modelo), ver perfil, y en rojo Reportar y
 * Bloquear (o Desbloquear si ya lo bloqueé). En la web es un menú desplegable
 * anclado al botón. Headless UI se encarga del teclado, de cerrar con Esc o
 * clic afuera y de devolver el foco al botón.
 */
export default function UserOptionsMenu({
  user,
  profileUrl,
  showFavorite = false,
  canBlock = true,
  blockedByMe = false,
  onReport,
  onBlock,
  onUnblock,
  onMyGifts,
  className = '',
}) {
  const [isFavorite, setIsFavorite] = useState(null);
  const [savingFavorite, setSavingFavorite] = useState(false);

  if (!user?.id) return null;

  const loadFavorite = () => {
    if (!showFavorite || isFavorite !== null) return;
    axios.get(`${config.REACT_APP_BASE_URL}/api/v1/home/getFavorite/${user.id}`)
      .then(({ data }) => setIsFavorite(!!data.isFavorite))
      .catch(() => setIsFavorite(false));
  };

  const toggleFavorite = async () => {
    setSavingFavorite(true);
    try {
      const { data } = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/home/storeFavorites/${user.id}`);
      setIsFavorite(!!data.isFavorite);
    } catch (err) {
      console.error('Error al actualizar favoritos:', err);
    } finally {
      setSavingFavorite(false);
    }
  };

  const item = (active, danger) =>
    `w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-left ${
      danger ? 'text-red-600 dark:text-red-400' : ''
    } ${active ? 'bg-gray-100 dark:bg-gray-700' : ''}`;

  return (
    <Menu as="div" className={`relative ${className}`}>
      <Menu.Button
        aria-label="Más opciones"
        onClick={loadFavorite}
        className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:opacity-70"
      >
        <LuMoreVertical className="text-xl" />
      </Menu.Button>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] origin-top-right rounded-xl bg-white dark:bg-gray-800 shadow-xl ring-1 ring-black/5 p-1.5 z-50 focus:outline-none text-gray-800 dark:text-gray-100">
          {showFavorite && (
            <Menu.Item>
              {({ active }) => (
                <button onClick={toggleFavorite} disabled={savingFavorite || isFavorite === null} className={item(active)}>
                  {savingFavorite || isFavorite === null
                    ? <span className="w-[18px] h-[18px] border-2 border-gray-200 border-t-rose-500 rounded-full animate-spin" />
                    : <LuHeart className={`text-lg ${isFavorite ? 'text-rose-500 fill-rose-500' : ''}`} />}
                  {isFavorite ? 'Quitar de mis favoritos' : 'Agregar a mis favoritos'}
                </button>
              )}
            </Menu.Item>
          )}
          {profileUrl && (
            <Menu.Item>
              {({ active }) => (
                <Link to={profileUrl} className={item(active)}>
                  <LuUser className="text-lg" /> Ver perfil
                </Link>
              )}
            </Menu.Item>
          )}
          {onMyGifts && (
            <Menu.Item>
              {({ active }) => (
                <button onClick={onMyGifts} className={item(active)}>
                  <LuGift className="text-lg" /> Mis regalos
                </button>
              )}
            </Menu.Item>
          )}
          {(showFavorite || profileUrl || onMyGifts) && <div className="my-1.5 border-t border-gray-200 dark:border-gray-700" />}
          <Menu.Item>
            {({ active }) => (
              <button onClick={onReport} className={item(active, true)}>
                <LuFlag className="text-lg" /> Reportar usuario
              </button>
            )}
          </Menu.Item>
          {canBlock && (
            <Menu.Item>
              {({ active }) => blockedByMe ? (
                <button onClick={onUnblock} className={item(active)}>
                  <LuShieldOff className="text-lg text-rose-500" /> Desbloquear usuario
                </button>
              ) : (
                <button onClick={onBlock} className={item(active, true)}>
                  <LuBan className="text-lg" /> Bloquear usuario
                </button>
              )}
            </Menu.Item>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
